<template>
    <TprmModal
        open
        :additional-class-name="additionalClassName"
        full-screen
        :show-close-icon="false"
        @close="close"
    >
        <template #header-slot>
            <header class="Modal__header">
                <div class="Modal__headerContent">
                    <DocumentDetailsModalHeader
                        :documents="documents"
                        :current-index="currentIndex"
                        :requirement-label="requirementLabel"
                        :uploaded-at="uploadedAt"
                        :email="email"
                        @select-document="selectDocument"
                        @previous="previousDocument"
                        @next="nextDocument"
                        @download="download"
                    >
                        <template #header-actions>
                            <slot name="header-actions" />
                        </template>
                    </DocumentDetailsModalHeader>
                    <NewButton
                        data-modal-close
                        variant="flat"
                        color="secondary"
                        size="icon-sm"
                        @click="close"
                    >
                        <EidIcon :icon-src="faXmark" />
                    </NewButton>
                </div>
            </header>
        </template>

        <DocumentDetailsContent
            :documents="documents"
            :current-index="currentIndex"
            @download="download"
        />
    </TprmModal>
</template>

<style lang="scss">
// stylelint-disable selector-max-compound-selectors, selector-max-combinators
.DocumentDetailsModal {
    .Modal__body {
        min-width: 0;
        box-sizing: border-box;
        gap: 0;
        padding: 0;
    }

    .Modal__headerContent {
        display: flex;
        align-items: center;
        align-self: stretch;
        flex: 1 0;
        gap: 20px;
        min-width: 0;
    }

    [data-modal-close] {
        margin-right: 20px;
    }

    .Modal__content {
        padding: 20px 0 !important;
        flex: 1;
        min-height: 0;
        max-height: none;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    &--pdf {
        .Modal__content {
            padding: 0 !important;
        }
    }

    &--single-document {
        .Modal__headerContent {
            // Needed only for single document — with multiple documents the header
            // is taller and already has enough padding.
            // !important because TprmModal has padding with !important.
            padding-top: 10px !important;
        }
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { downloadFileUsingBase64String } from '@evidentid/file-utils/blobs';
    import { mimetypeExtensions } from '@evidentid/file-utils/mimeTypes';
    import { sanitizeFileName } from '@evidentid/file-utils/sanitizeFileName';
    import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    import DocumentDetailsContent from './DocumentDetailsContent.vue';
    import DocumentDetailsModalHeader from './DocumentDetailsModalHeader.vue';

    // PRODUCT-26110 QA fixes4: declares the `close` event this component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'close' but it is
    // neither declared in the emits option nor as an 'onClose' prop" every time it fires.
    @Component({
        emits: [ 'close' ],
        components: {
            TprmModal,
            NewButton,
            EidIcon,
            DocumentDetailsModalHeader,
            DocumentDetailsContent,
        },
    })
    export default class DocumentDetailsModal extends Vue {
        private faXmark = faXmark;

        @Prop({ type: String })
        private email!: string;

        @Prop({ type: Array, required: true })
        private documents!: VerificationDocument[];

        @Prop({ type: String })
        private uploadedAt!: string;

        @Prop({ type: String })
        private requirementLabel!: string;

        private currentIndex = 0;

        @Watch('documents', { immediate: true })
        private onDocumentsChange(): void {
            if (this.currentIndex >= this.documents.length) {
                this.currentIndex = 0;
            }
        }

        private get additionalClassName(): string {
            const pdfClass = this.isPdf ? ' DocumentDetailsModal--pdf' : '';
            const singleDocumentClass = this.documents.length === 1 ? ' DocumentDetailsModal--single-document' : '';
            return `DocumentDetailsModal${pdfClass}${singleDocumentClass}`;
        }

        private get isPdf(): boolean {
            const currentDocument = this.documents[this.currentIndex];
            return currentDocument?.mimeType === 'application/pdf';
        }

        private selectDocument(index: number): void {
            if (index >= 0 && index < this.documents.length) {
                this.currentIndex = index;
            }
        }

        private download(index: number): void {
            const { mimeType, data, filename } = this.documents[index];
            if (filename) {
                downloadFileUsingBase64String(data, sanitizeFileName(filename));
                return;
            }
            const extension = mimetypeExtensions[mimeType] ?? '';
            const suffix = this.documents.length > 1 ? ` - File ${index + 1}` : '';
            const fileName = `${this.email} - Document${suffix}${extension}`;
            downloadFileUsingBase64String(data, sanitizeFileName(fileName));
        }

        private previousDocument(): void {
            if (this.currentIndex > 0) {
                this.currentIndex--;
            }
        }

        private nextDocument(): void {
            if (this.currentIndex < this.documents.length - 1) {
                this.currentIndex++;
            }
        }

        private close(): void {
            this.$emit('close');
        }
    }
</script>
